/**
 * Dialog — accessible modal dialog built on Radix UI.
 *
 * Focus is trapped while open and restored to the trigger on close.
 * The backdrop dismisses by click and by `Escape`. Entrance / exit
 * animations are driven by Radix's `data-state` selectors targeted by
 * the `tailwindcss-animate` utilities, so timings line up with the
 * other primitives without re-deriving keyframes per component.
 *
 * Usage:
 *
 *     <Dialog>
 *       <DialogTrigger asChild>
 *         <button type="button">Explain</button>
 *       </DialogTrigger>
 *       <DialogContent>
 *         <DialogTitle>Why was this neutralized?</DialogTitle>
 *         <DialogDescription>
 *           The SEPARATE guarantee makes this attack structurally inexpressible.
 *         </DialogDescription>
 *         <DialogClose asChild>
 *           <button type="button">Close</button>
 *         </DialogClose>
 *       </DialogContent>
 *     </Dialog>
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../styling/index.js";

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export type DialogOverlayProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>;
export type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>;
export type DialogTitleProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>;
export type DialogDescriptionProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Description
>;

export const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  DialogOverlayProps
>(function DialogOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-40 bg-neutral-900/50 backdrop-blur-sm",
        "data-[state=open]:animate-in data-[state=open]:fade-in-0",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
        className,
      )}
      {...props}
    />
  );
});

export const DialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(function DialogContent({ className, children, ...props }, ref) {
  return (
    <DialogPrimitive.Portal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2",
          "w-[min(32rem,calc(100vw-2rem))] rounded-lg border border-neutral-300 bg-white p-6",
          "text-sm leading-snug text-neutral-900 shadow-xl outline-none",
          "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          className,
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});

export const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  DialogTitleProps
>(function DialogTitle({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn("text-base font-semibold text-neutral-900", className)}
      {...props}
    />
  );
});

export const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  DialogDescriptionProps
>(function DialogDescription({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn("mt-2 text-sm text-neutral-700", className)}
      {...props}
    />
  );
});
